import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, Save, Plus, Eye, Trash2, Upload, AlertCircle } from 'lucide-react';
import { ImageGalleryManager, GalleryImage } from '@/components/admin/ImageGalleryManager';
import { SectionImageGalleryManager } from '@/components/admin/SectionImageGalleryManager';
import { Badge } from '@/components/ui/badge';

interface ContentSection {
  heading: string;
  content: string;
  image_url?: string; // Legacy single image - kept for backward compatibility
  gallery_images?: GalleryImage[]; // New multi-image support
}

interface TownData {
  id: string;
  name: string;
  slug: string;
  state: string | null;
  hero_headline: string | null;
  hero_subheadline: string | null;
  hero_image_url: string | null;
  hero_image_attribution: string | null;
  gallery_images: GalleryImage[] | null;
  intro_summary: string | null;
  about_town: string | null;
  location_sections: ContentSection[] | null;
  amenities_sections: ContentSection[] | null;
  content_state: string | null;
  draft_content: any | null;
  published_content: any | null;
}

export default function TownContentEdit() {
  const { townId } = useParams<{ townId: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [town, setTown] = useState<TownData | null>(null);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  
  // Form state
  const [heroHeadline, setHeroHeadline] = useState('');
  const [heroSubheadline, setHeroSubheadline] = useState('');
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [introSummary, setIntroSummary] = useState('');
  const [aboutTown, setAboutTown] = useState('');
  const [locationSections, setLocationSections] = useState<ContentSection[]>([]);
  const [amenitiesSections, setAmenitiesSections] = useState<ContentSection[]>([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }

    if (user && townId) {
      fetchTown();
    }
  }, [user, authLoading, townId, navigate]);

  const fetchTown = async () => {
    try {
      const { data, error } = await supabase
        .from('towns')
        .select('id, name, slug, state, hero_headline, hero_subheadline, hero_image_url, hero_image_attribution, gallery_images, intro_summary, about_town, location_sections, amenities_sections, content_state, draft_content, published_content')
        .eq('id', townId)
        .single();

      if (error) throw error;

      // Cast JSONB fields properly and migrate legacy single images to gallery_images
      const migrateSection = (section: any): ContentSection => {
        const migrated: ContentSection = {
          heading: section.heading || '',
          content: section.content || '',
        };
        // Migrate legacy image_url to gallery_images if needed
        if (section.gallery_images && Array.isArray(section.gallery_images)) {
          migrated.gallery_images = section.gallery_images;
        } else if (section.image_url) {
          migrated.gallery_images = [{ url: section.image_url, attribution: '' }];
        } else {
          migrated.gallery_images = [];
        }
        return migrated;
      };

      const townData: TownData = {
        ...data,
        location_sections: Array.isArray(data.location_sections) 
          ? (data.location_sections as unknown as any[]).map(migrateSection)
          : null,
        amenities_sections: Array.isArray(data.amenities_sections) 
          ? (data.amenities_sections as unknown as any[]).map(migrateSection)
          : null,
        gallery_images: Array.isArray(data.gallery_images)
          ? (data.gallery_images as unknown as GalleryImage[])
          : null,
        content_state: data.content_state,
        draft_content: data.draft_content,
        published_content: data.published_content,
      };

      // Check for unpublished changes
      const draftJson = JSON.stringify({
        hero_headline: data.hero_headline,
        hero_subheadline: data.hero_subheadline,
        intro_summary: data.intro_summary,
        about_town: data.about_town,
        location_sections: data.location_sections,
        amenities_sections: data.amenities_sections,
      });
      const publishedJson = data.published_content ? JSON.stringify(data.published_content) : null;
      setHasUnpublishedChanges(!!publishedJson && draftJson !== publishedJson);

      setTown(townData);
      setHeroHeadline(townData.hero_headline || '');
      setHeroSubheadline(townData.hero_subheadline || '');
      
      // Convert existing hero image + gallery to unified gallery
      const existingGallery: GalleryImage[] = townData.gallery_images || [];
      if (townData.hero_image_url && existingGallery.length === 0) {
        // Migrate existing hero image to gallery
        setGalleryImages([{ url: townData.hero_image_url, attribution: townData.hero_image_attribution || '' }]);
      } else {
        setGalleryImages(existingGallery);
      }
      
      setIntroSummary(townData.intro_summary || '');
      setAboutTown(townData.about_town || '');
      setLocationSections(townData.location_sections || []);
      setAmenitiesSections(townData.amenities_sections || []);
    } catch (error) {
      console.error('Error fetching town:', error);
      toast({
        title: 'Error loading town',
        description: 'Could not load town data',
        variant: 'destructive',
      });
      navigate('/admin/towns');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!town) return;

    setSaving(true);
    try {
      // First image becomes the hero image for backward compatibility
      const heroImage = galleryImages[0] || null;
      
      const { error } = await supabase
        .from('towns')
        .update({
          hero_headline: heroHeadline || null,
          hero_subheadline: heroSubheadline || null,
          hero_image_url: heroImage?.url || null,
          hero_image_attribution: heroImage?.attribution || null,
          gallery_images: galleryImages as unknown as any,
          intro_summary: introSummary || null,
          about_town: aboutTown || null,
          location_sections: locationSections as unknown as any,
          amenities_sections: amenitiesSections as unknown as any,
          content_state: 'draft',
        })
        .eq('id', town.id);

      if (error) throw error;
      setHasUnpublishedChanges(true);

      toast({
        title: 'Content saved!',
        description: 'Your changes have been saved successfully.',
      });
    } catch (error: any) {
      console.error('Error saving:', error);
      toast({
        title: 'Error saving',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!town) return;

    setPublishing(true);
    try {
      // First save current changes
      await handleSave();

      // Create published content snapshot
      const publishedContent = {
        hero_headline: heroHeadline,
        hero_subheadline: heroSubheadline,
        intro_summary: introSummary,
        about_town: aboutTown,
        location_sections: locationSections,
        amenities_sections: amenitiesSections,
        gallery_images: galleryImages,
      };

      const { error } = await supabase
        .from('towns')
        .update({
          published_content: publishedContent as unknown as any,
          content_state: 'published',
        })
        .eq('id', town.id);

      if (error) throw error;

      setHasUnpublishedChanges(false);
      toast({
        title: 'Published!',
        description: 'Town content is now live on your site.',
      });
    } catch (error: any) {
      console.error('Error publishing:', error);
      toast({
        title: 'Error publishing',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  };

  const updateSection = (
    sectionType: 'location' | 'amenities',
    index: number,
    field: 'heading' | 'content',
    value: string
  ) => {
    if (sectionType === 'location') {
      setLocationSections(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    } else {
      setAmenitiesSections(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    }
  };

  const updateSectionImages = (
    sectionType: 'location' | 'amenities',
    index: number,
    images: GalleryImage[]
  ) => {
    if (sectionType === 'location') {
      setLocationSections(prev => {
        const updated = [...prev];
        updated[index] = { 
          ...updated[index], 
          gallery_images: images,
          image_url: images[0]?.url // Keep legacy field in sync
        };
        return updated;
      });
    } else {
      setAmenitiesSections(prev => {
        const updated = [...prev];
        updated[index] = { 
          ...updated[index], 
          gallery_images: images,
          image_url: images[0]?.url // Keep legacy field in sync
        };
        return updated;
      });
    }
  };

  const addSection = (sectionType: 'location' | 'amenities') => {
    const newSection: ContentSection = { heading: '', content: '', gallery_images: [] };
    if (sectionType === 'location') {
      setLocationSections(prev => [...prev, newSection]);
    } else {
      setAmenitiesSections(prev => [...prev, newSection]);
    }
  };

  const removeSection = (sectionType: 'location' | 'amenities', index: number) => {
    if (sectionType === 'location') {
      setLocationSections(prev => prev.filter((_, i) => i !== index));
    } else {
      setAmenitiesSections(prev => prev.filter((_, i) => i !== index));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !town) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/towns')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <span className="font-display text-xl">Edit Content</span>
                <span className="text-muted-foreground ml-2">— {town.name}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasUnpublishedChanges && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Unpublished Changes
                </Badge>
              )}
              {!hasUnpublishedChanges && town.published_content && (
                <Badge variant="default">Published</Badge>
              )}
              <Button 
                variant="outline" 
                onClick={() => window.open(`/${town.slug}?preview=true`, '_blank')}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              {hasUnpublishedChanges && (
                <Button variant="default" onClick={handlePublish} disabled={publishing}>
                  {publishing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Publish
                </Button>
              )}
              <Button variant="gold" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Draft
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Hero Section */}
        <Card>
          <CardHeader>
            <CardTitle>Hero Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Image Gallery */}
            <ImageGalleryManager
              images={galleryImages}
              onChange={setGalleryImages}
              storagePath={`towns/${town.id}`}
              label="Town Images"
            />

            <div>
              <Label htmlFor="heroHeadline">Headline</Label>
              <Input
                id="heroHeadline"
                value={heroHeadline}
                onChange={(e) => setHeroHeadline(e.target.value)}
                placeholder={`Real Estate in ${town.name}, ${town.state || 'MA'}`}
              />
            </div>
            <div>
              <Label htmlFor="heroSubheadline">Subheadline</Label>
              <Textarea
                id="heroSubheadline"
                value={heroSubheadline}
                onChange={(e) => setHeroSubheadline(e.target.value)}
                placeholder="Discover homes and properties..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* About Section */}
        <Card>
          <CardHeader>
            <CardTitle>About {town.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="introSummary">Intro Summary (short)</Label>
              <Textarea
                id="introSummary"
                value={introSummary}
                onChange={(e) => setIntroSummary(e.target.value)}
                placeholder="A brief one-line summary..."
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="aboutTown">About (HTML allowed)</Label>
              <Textarea
                id="aboutTown"
                value={aboutTown}
                onChange={(e) => setAboutTown(e.target.value)}
                placeholder="<p>Detailed description...</p>"
                rows={6}
                className="font-mono text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Location Sections */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Location & Access</CardTitle>
            <Button variant="outline" size="sm" onClick={() => addSection('location')}>
              <Plus className="h-4 w-4 mr-1" />
              Add Section
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {locationSections.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No location sections yet. Click "Add Section" to create one.
              </p>
            ) : (
              locationSections.map((section, index) => (
                <SectionEditor
                  key={index}
                  section={section}
                  index={index}
                  sectionType="location"
                  storagePath={`towns/${town.id}`}
                  onUpdate={updateSection}
                  onRemove={removeSection}
                  onUpdateImages={updateSectionImages}
                />
              ))
            )}
          </CardContent>
        </Card>

        {/* Amenities Sections */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Schools & Amenities</CardTitle>
            <Button variant="outline" size="sm" onClick={() => addSection('amenities')}>
              <Plus className="h-4 w-4 mr-1" />
              Add Section
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {amenitiesSections.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No amenities sections yet. Click "Add Section" to create one.
              </p>
            ) : (
              amenitiesSections.map((section, index) => (
                <SectionEditor
                  key={index}
                  section={section}
                  index={index}
                  sectionType="amenities"
                  storagePath={`towns/${town.id}`}
                  onUpdate={updateSection}
                  onRemove={removeSection}
                  onUpdateImages={updateSectionImages}
                />
              ))
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// Section Editor Component
interface SectionEditorProps {
  section: ContentSection;
  index: number;
  sectionType: 'location' | 'amenities';
  storagePath: string;
  onUpdate: (type: 'location' | 'amenities', index: number, field: 'heading' | 'content', value: string) => void;
  onRemove: (type: 'location' | 'amenities', index: number) => void;
  onUpdateImages: (type: 'location' | 'amenities', index: number, images: GalleryImage[]) => void;
}

function SectionEditor({
  section,
  index,
  sectionType,
  storagePath,
  onUpdate,
  onRemove,
  onUpdateImages,
}: SectionEditorProps) {
  return (
    <div className="border rounded-lg p-4 space-y-4 bg-background">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-4">
          <div>
            <Label>Section Heading</Label>
            <Input
              value={section.heading}
              onChange={(e) => onUpdate(sectionType, index, 'heading', e.target.value)}
              placeholder="e.g., Commute Times"
            />
          </div>
          <div>
            <Label>Content (HTML allowed)</Label>
            <Textarea
              value={section.content}
              onChange={(e) => onUpdate(sectionType, index, 'content', e.target.value)}
              placeholder="<p>Content here...</p>"
              rows={4}
              className="font-mono text-sm"
            />
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
          onClick={() => onRemove(sectionType, index)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Image Gallery Section */}
      <div className="border-t pt-4">
        <SectionImageGalleryManager
          images={section.gallery_images || []}
          onChange={(images) => onUpdateImages(sectionType, index, images)}
          storagePath={`${storagePath}/${sectionType}_${index}`}
        />
      </div>
    </div>
  );
}
